import { type FormEvent, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { useAuth } from '@langmail/shared-ui'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { useAddressAvailability } from '../hooks/use-address-availability'
import { type AvailabilityReason, waitForMailboxActive } from '../native-mailbox-api'

const MIN_PASSWORD_LENGTH = 8

const UNAVAILABLE_MESSAGE: Record<AvailabilityReason, string> = {
  invalid: 'Use lowercase letters, numbers, dots, hyphens or underscores.',
  reserved: 'That address is reserved.',
  taken: 'That address is already taken.',
}

interface StandaloneSignupFormProps {
  // Releases that suppression when sign-up itself fails before a session exists.
  // Mailbox failed/timeout exits keep the gate held because the user is already
  // signed in and should not be redirected before the mailbox is active.
  onSubmitEnd?: () => void
  // Signals the page that this flow now owns navigation, so its generic
  // "already logged in → land" redirect stays suppressed until the mailbox is
  // active (Decision 6). Called when provisioning begins.
  onSubmitStart?: () => void
}

export const StandaloneSignupForm = ({ onSubmitStart, onSubmitEnd }: StandaloneSignupFormProps) => {
  const { signUpWithLangmail } = useAuth()
  const navigate = useNavigate()
  // The pricing page's trial button lands here with `?plan=`; carry it to the
  // success page so the chosen cadence reaches the subscribe page.
  const [searchParams] = useSearchParams()
  const plan = searchParams.get('plan')
  const signUpEstablishedSession = useRef(false)

  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [recoveryEmail, setRecoveryEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Distinct from availability "checking": this is the post-submit spinner that
  // covers sign-up + mailbox provisioning.
  const [provisioning, setProvisioning] = useState(false)

  const availability = useAddressAvailability(username)

  const canSubmit =
    availability.status === 'available' &&
    displayName.trim().length > 0 &&
    password.length >= MIN_PASSWORD_LENGTH &&
    password === confirmPassword &&
    !provisioning

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setProvisioning(true)
    // Take over navigation before the session appears, so the page doesn't land
    // the user before the mailbox finishes provisioning.
    onSubmitStart?.()
    const { error: signUpError } = await signUpWithLangmail({
      username,
      password,
      displayName: displayName.trim(),
      recoveryEmail: recoveryEmail.trim() || undefined,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    if (signUpError) {
      setError(signUpError.message)
      setProvisioning(false)
      if (!signUpEstablishedSession.current) {
        onSubmitEnd?.()
      }
      return
    }
    signUpEstablishedSession.current = true

    // Sign-up succeeded and signed the user in; wait for the mailbox to finish
    // provisioning before landing so the settings page shows a live address.
    const outcome = await waitForMailboxActive()
    if (outcome === 'active') {
      navigate(plan ? `/success?plan=${encodeURIComponent(plan)}` : '/success', { replace: true })
      return
    }
    setError(
      outcome === 'failed'
        ? 'We could not set up your mailbox. Please try again.'
        : 'Setting up your mailbox is taking longer than expected. Please try again.'
    )
    setProvisioning(false)
  }

  return (
    <form className="mt-6 space-y-4 text-left" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <Label htmlFor="username">Address</Label>
        <div className="flex items-center gap-2">
          <Input
            autoCapitalize="none"
            autoComplete="username"
            autoCorrect="off"
            id="username"
            onChange={(event) => setUsername(event.target.value)}
            placeholder="alice"
            spellCheck={false}
            value={username}
          />
          <span className="font-mono text-muted-foreground text-sm">@langmail.me</span>
        </div>
        <AddressHint state={availability} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="displayName">Display name</Label>
        <Input
          autoComplete="name"
          id="displayName"
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Alice Smith"
          value={displayName}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">Password</Label>
        <Input
          autoComplete="new-password"
          id="password"
          onChange={(event) => setPassword(event.target.value)}
          placeholder="At least 8 characters"
          type="password"
          value={password}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="confirmPassword">Confirm password</Label>
        <Input
          autoComplete="new-password"
          id="confirmPassword"
          onChange={(event) => setConfirmPassword(event.target.value)}
          type="password"
          value={confirmPassword}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="recoveryEmail">Recovery email (optional)</Label>
        <Input
          autoComplete="email"
          id="recoveryEmail"
          onChange={(event) => setRecoveryEmail(event.target.value)}
          placeholder="you@example.com"
          type="email"
          value={recoveryEmail}
        />
        <p className="text-muted-foreground text-xs">Where we send password-reset links. You can add this later.</p>
      </div>

      {error ? <p className="text-destructive text-sm">{error}</p> : null}

      <Button
        className="w-full rounded-lg bg-primary px-8 py-4 text-[15px] text-primary-foreground hover:bg-primary/90"
        disabled={!canSubmit}
        type="submit"
      >
        {provisioning ? 'Setting up your mailbox…' : 'Create address'}
      </Button>
    </form>
  )
}

const AddressHint = ({ state }: { state: ReturnType<typeof useAddressAvailability> }) => {
  if (state.status === 'idle') {
    return null
  }
  if (state.status === 'checking') {
    return <p className="font-mono text-muted-foreground text-xs">Checking…</p>
  }
  if (state.status === 'available') {
    // Teal Sage success accent.
    return <p className="font-mono text-[#4E8A76] text-xs dark:text-[#7FB0A0]">Available</p>
  }
  if (state.status === 'error') {
    return <p className="font-mono text-muted-foreground text-xs">Couldn’t check availability.</p>
  }
  return <p className="text-destructive text-xs">{UNAVAILABLE_MESSAGE[state.reason]}</p>
}
